/**
 * A `userErrors` entry, in the reader's language.
 *
 * The API sends `{ code, message, params, line }` and does not localize
 * (V1.md section 18): a resolver has a language, but the markdown validator is
 * a pure module that does not, and parse diagnostics are the bulk of what
 * arrives here. So the code is the key and the params are the values, and this
 * is the one place that turns the pair into a sentence.
 *
 * A code this build has no entry for — a newer API against an older bundle —
 * reads as a general refusal in the page's language, never as the server's
 * `message`. That message is English whatever the page is, and it is written
 * for a developer: a Spanish page must not show it, and neither should an
 * English one. The missing entry is still a defect, which is why it is
 * reported.
 */
import { lookup, DICTIONARIES, type TranslationKey } from '@athena/i18n'
import { useLanguage, useTranslate } from '@athena/ui'

import type { Language } from './language.js'

export interface ApiUserError {
  code: string
  message: string
  params: readonly string[]
  line?: number | null
}

/**
 * Whether the dictionary has a sentence for this code.
 *
 * Checked against the dictionary rather than a hand-kept list of codes: the
 * API owns which codes exist, and a list here would be a second copy of it
 * that drifts. `lookup` is also what keeps `t` from reporting a missing key on
 * a path we are deliberately probing.
 */
function keyFor(language: Language, code: string): TranslationKey | null {
  const key = `userError.${code}`
  return lookup(DICTIONARIES[language], key) === undefined ? null : (key as TranslationKey)
}

/**
 * `(error) => string`, bound to the page's language.
 *
 * A hook rather than a component because the callers are not all rendering
 * text directly — several put the result into component state, and one hands a
 * whole list of them to the upload area.
 */
export function useUserErrorText(): (error: ApiUserError) => string {
  const t = useTranslate()
  const language = useLanguage()

  return function text(error: ApiUserError): string {
    const key = keyFor(language, error.code)
    if (!key) {
      // Loud, for the same reason a missing translation key is loud: a code
      // rendering in English on a Spanish page is a defect, not a feature.
      console.error(`[i18n] no userError entry for code ${error.code}`)
      return t('ui.failure.invalid')
    }
    return t(key, [...error.params])
  }
}

/**
 * The first error's text, or a fallback of the caller's choosing.
 *
 * The shape almost every mutation handler wants: `userErrors[0]` when the
 * server refused for a reason it named, and the screen's own wording when the
 * mutation came back empty-handed for a reason nobody named.
 */
export function useFirstUserErrorText(): (
  errors: readonly ApiUserError[] | undefined,
  fallback: string,
) => string {
  const text = useUserErrorText()

  return (errors, fallback) => {
    const first = errors?.[0]
    return first ? text(first) : fallback
  }
}
